import React, { useState } from 'react';
import { Letter } from 'react-letter';
import { splitHtmlQuote, splitTextQuote } from '../utils/quoteSplitter';
import SentMailFrame from '../../SentMailFrame';

/**
 * MailBody — renders the actual email content safely.
 *
 * Strategy mirrors the classic inbox:
 *   - If a pre-sanitized HTML snapshot exists in Storage (`htmlStorageUrl`),
 *     render it in a sandboxed iframe (most faithful to the original). We can't
 *     touch a cross-origin iframe, so quote-collapsing doesn't apply here.
 *   - For our OWN sent mail (`email.isSent`), render the raw stored HTML in a
 *     sandboxed `srcDoc` iframe. This is a 1:1 copy of what actually left the
 *     server: the footer image loads directly (no broken /api/proxy-image) and
 *     the app's global `* { margin: 0 }` reset can't leak in and squash the
 *     footer spacing — so the preview matches what the recipient sees.
 *   - Otherwise fall back to `react-letter`, which sanitizes inline HTML. Here we
 *     split off the quoted history and tuck it behind a "…" toggle (Gmail style)
 *     so the embedded chain doesn't repeat what the thread already shows.
 */

const LETTER_PROPS = {
  // react-letter only applies a wrapper class when one is passed (it no longer
  // defaults to `safe-email-content`). Our CSS — including the paragraph/list
  // spacing that the global `* { margin: 0 }` reset would otherwise strip —
  // hangs off this class, so set it explicitly to keep mail bodies formatted.
  className: 'safe-email-content',
  useIframe: false,
  rewriteExternalLinks: (url) => `/api/proxy-link?url=${encodeURIComponent(url)}`,
  rewriteExternalResources: (url) => `/api/proxy-image?url=${encodeURIComponent(url)}`,
  allowedSchemas: ['http', 'https', 'mailto'],
  preserveCssPriority: true,
};

export default function MailBody({ email }) {
  const [showQuote, setShowQuote] = useState(false);

  const htmlContent = email.bodyHtml || email.content || '';
  const textContent = email.bodyText || email.text || '';

  if (!htmlContent && !textContent && !email.htmlStorageUrl) {
    return <div className="ivx-body-empty">No content available</div>;
  }

  if (email.htmlStorageUrl) {
    return (
      <div className="ivx-body-iframe-wrap">
        <iframe
          src={email.htmlStorageUrl}
          className="ivx-body-iframe"
          title="Email content"
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      </div>
    );
  }

  // Our own outgoing mail (sent, scheduled, forwarded, or an outgoing message
  // inside a thread) → render the exact HTML that left / will leave the server.
  // The stored HTML already carries direct Firebase Storage image URLs, so the
  // SentMailFrame loads them 1:1 (no broken /api/proxy-image rewrite from the
  // react-letter path) and the preview matches the recipient's view.
  const isOwnOutgoing =
    email.isSent === true ||
    email.isScheduled === true ||
    email.isForwarded === true ||
    email.isOutgoing === true;
  if (isOwnOutgoing && htmlContent) {
    return (
      <div className="ivx-body-iframe-wrap">
        <SentMailFrame html={htmlContent} title={email.subject} />
      </div>
    );
  }

  // Separate the new content from the quoted history.
  const useHtml = !!htmlContent;
  const split = useHtml ? splitHtmlQuote(htmlContent) : splitTextQuote(textContent);
  const hasQuote = split.hasQuote;
  const mainHtml = useHtml ? split.mainHtml : '';
  const mainText = useHtml ? '' : split.mainText;
  const quotedHtml = useHtml ? split.quotedHtml : '';
  const quotedText = useHtml ? '' : split.quotedText;

  return (
    <div className="ivx-body-letter">
      <Letter html={mainHtml} text={mainText} iframeTitle={email.subject} {...LETTER_PROPS} />

      {hasQuote && (
        <div className="ivx-body-quote">
          <button
            type="button"
            className="ivx-quote-toggle"
            onClick={() => setShowQuote((v) => !v)}
            aria-expanded={showQuote}
            title={showQuote ? 'Zitierten Verlauf ausblenden' : 'Zitierten Verlauf anzeigen'}
          >
            {showQuote ? 'Zitierten Verlauf ausblenden' : '\u00B7\u00B7\u00B7'}
          </button>
          {showQuote && (
            <div className="ivx-body-quote-content">
              <Letter html={quotedHtml} text={quotedText} {...LETTER_PROPS} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
